import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import ProductGroupSelect from '@/components/Form/Shared/ProductGroupSelect'
import Paper from '@/components/Paper'
import { useFilterParams } from '@/hooks/useFilterParams'
import {
    CONTRACT_NDA_OPTIONS,
    CONTRACT_SLA_OPTIONS,
} from '@/modules/Contract/constants'
import { exportOverdueContracts } from '@/modules/Contract/services/customer'
import { INDUSTRIES } from '@/modules/Customer/constants'
import dayjs from 'dayjs'
import { Download } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, FormItem } from 'vnetwork-ui'
import ContractOverviewAllCard from './AllCard'

const today = dayjs()

const formatDateRangeTitle = (value: unknown) => {
    if (!Array.isArray(value) || !value[0]) return ''
    const from = dayjs(value[0])
    const to = value[1] ? dayjs(value[1]) : null
    const fromText = from.isValid() ? from.format('DD/MM/YYYY') : ''
    const toText = to?.isValid() ? to.format('DD/MM/YYYY') : ''
    return toText ? `${fromText} - ${toText}` : fromText
}

const Overview = () => {
    const { t } = useTranslation()
    const [isExporting, setIsExporting] = useState(false)
    const { methods, encodedFilters } = useFilterParams('contracts.overview', {
        customer_industry: [],
        employee_id: [],
        sla_enable: [],
        nda_enable: [],
        product_group_name: [],
        created_at: [
            today.startOf('month').toDate(),
            today.endOf('month').toDate(),
        ],
    })
    const createdAtTitle = formatDateRangeTitle(methods.watch('created_at'))

    const handleExportOverdue = async () => {
        setIsExporting(true)
        try {
            await exportOverdueContracts()
            toast.success(t('export-success') || 'Xuất file thành công')
        } catch (error) {
            console.error('Export failed:', error)
            toast.error(t('export-failed') || 'Xuất file thất bại')
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className="space-y-2">
            <Paper className="mt-2 flex flex-col gap-3 p-2">
                <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2 bg-white min-w-0">
                        <FormItem
                            name="customer_industry"
                            type="select"
                            multiple
                            options={INDUSTRIES}
                            control={methods.control}
                            placeholder={t('customer_industry')}
                        />
                    </div>
                    <div className="col-span-2 bg-white min-w-0">
                        <FormItem
                            name="sla_enable"
                            type="select"
                            multiple
                            options={CONTRACT_SLA_OPTIONS(t)}
                            control={methods.control}
                            placeholder={t('sla')}
                        />
                    </div>
                    <div className="col-span-2 bg-white min-w-0">
                        <FormItem
                            name="nda_enable"
                            type="select"
                            multiple
                            options={CONTRACT_NDA_OPTIONS(t)}
                            control={methods.control}
                            placeholder={t('nda')}
                        />
                    </div>
                    <div className="col-span-2 bg-white min-w-0">
                        <EmployeeSelect
                            multiple
                            showLabel={false}
                            name="employee_id"
                            control={methods.control}
                        />
                    </div>
                    <div className="col-span-2 bg-white min-w-0">
                        <ProductGroupSelect
                            multiple
                            showLabel={false}
                            name="product_group_name"
                            control={methods.control}
                        />
                    </div>
                    <div
                        className="col-span-2 bg-white min-w-0"
                        title={createdAtTitle}
                    >
                        <FormItem
                            className="w-full min-w-0 [&_[data-ui-component='TextField']]:w-full [&_.text-field-container]:w-full [&_.text-field-container]:min-w-0 [&_.text-field]:min-w-0 [&_.text-field]:pr-10 [&_.text-field]:overflow-hidden [&_.text-field]:text-ellipsis [&_.text-field]:whitespace-nowrap"
                            name="created_at"
                            type="daterangepicker"
                            control={methods.control}
                            placeholder={t('created_at')}
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end">
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleExportOverdue}
                        loading={isExporting}
                        startIcon={<Download className="w-4 h-4" />}
                    >
                        {t('export-overdue-contracts') ||
                            'Xuất hợp đồng quá hạn'}
                    </Button>
                </div>
            </Paper>
            <ContractOverviewAllCard encodedFilters={encodedFilters} />
        </div>
    )
}

export default Overview
